import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Button, Card, Form, Spinner } from 'react-bootstrap';
import { Link, useParams } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeHighlight from 'rehype-highlight';
import AdminLayout from '../components/AdminLayout';
import api from '../lib/api';
import 'highlight.js/styles/github-dark.css';

function formatTime(value) {
  if (!value) return '-';

  try {
    return new Date(value).toLocaleString();
  } catch {
    return value;
  }
}

function getRoleLabel(role) {
  if (role === 'user') return 'Visitor';
  if (role === 'assistant') return 'AI Agent';
  if (role === 'agent') return 'Human Agent';
  return role || 'Message';
}

function getBubbleStyle(role) {
  if (role === 'user') {
    return {
      alignSelf: 'flex-start',
      background: '#ffffff',
      border: '1px solid #e2e8f0',
      color: '#0f172a',
    };
  }

  if (role === 'assistant') {
    return {
      alignSelf: 'flex-end',
      background: '#eef2ff',
      border: '1px solid #c7d2fe',
      color: '#1e1b4b',
    };
  }

  return {
    alignSelf: 'flex-end',
    background: '#dbeafe',
    border: '1px solid #bfdbfe',
    color: '#0f172a',
  };
}

function MessageBubble({ message }) {
  const markdownPlugins = useMemo(() => [remarkGfm], []);
  const rehypePlugins = useMemo(() => [rehypeHighlight], []);

  const text = String(message?.text || '').trim();

  return (
    <div
      className="rounded-4 px-3 py-2"
      style={{
        ...getBubbleStyle(message?.role),
        maxWidth: '86%',
        boxShadow: '0 10px 24px rgba(15, 23, 42, 0.06)',
      }}
    >
      <div className="d-flex justify-content-between align-items-center gap-3 mb-1">
        <div className="small text-uppercase fw-semibold text-muted">
          {getRoleLabel(message?.role)}
        </div>
        <div className="small text-muted" style={{ whiteSpace: 'nowrap' }}>
          {formatTime(message?.created_at)}
        </div>
      </div>

      <div className="handover-markdown">
        <ReactMarkdown remarkPlugins={markdownPlugins} rehypePlugins={rehypePlugins}>
          {text || ' '}
        </ReactMarkdown>
      </div>
    </div>
  );
}

export default function AdminHandoverThreadPage() {
  const { handoverId } = useParams();

  const [handover, setHandover] = useState(null);
  const [messages, setMessages] = useState([]);
  const [agents, setAgents] = useState([]);
  const [selectedAgentId, setSelectedAgentId] = useState('');
  const [initialLoading, setInitialLoading] = useState(true);
  const [msg, setMsg] = useState('');
  const [reply, setReply] = useState('');
  const [sending, setSending] = useState(false);

  const listRef = useRef(null);

  const selectedAgent = useMemo(() => (
    agents.find((agent) => String(agent.id) === String(selectedAgentId)) || null
  ), [agents, selectedAgentId]);

  const scrollToBottom = useCallback(() => {
    requestAnimationFrame(() => {
      if (!listRef.current) return;
      listRef.current.scrollTop = listRef.current.scrollHeight;
    });
  }, []);

  const loadAgents = useCallback(async () => {
    try {
      const { data } = await api.get('/admin/settings/support-agents/');
      const activeAgents = Array.isArray(data)
        ? data.filter((agent) => agent.is_active)
        : [];
      setAgents(activeAgents);
    } catch {
      setAgents([]);
    }
  }, []);

  const load = useCallback(async ({ silent = false } = {}) => {
    try {
      if (!silent) {
        setInitialLoading(true);
      }

      const { data } = await api.get(`/agent/handover/${handoverId}/conversation/`);

      const loadedHandover = data?.handover || null;
      const loadedMessages = Array.isArray(data?.messages) ? data.messages : [];

      setHandover(loadedHandover);
      setMessages(loadedMessages);
      setMsg('');

      if (loadedHandover?.picked_by && !selectedAgentId) {
        setSelectedAgentId(String(loadedHandover.picked_by));
      }

      scrollToBottom();
    } catch (e) {
      setMsg(e?.response?.data?.error || 'Failed to load handover conversation.');
    } finally {
      if (!silent) {
        setInitialLoading(false);
      }
    }
  }, [handoverId, scrollToBottom, selectedAgentId]);

  const sendReply = async () => {
    const message = reply.trim();

    if (!message || !handover?.id) return;

    try {
      setSending(true);

      const { data } = await api.post(`/agent/handover/${handover.id}/reply/`, {
        message,
        agent_id: selectedAgentId || handover?.picked_by || undefined,
      });

      const chatLogId = data?.chat_log_id || Date.now();

      setMessages((prev) => [
        ...prev,
        {
          id: `a-${chatLogId}`,
          chat_log_id: chatLogId,
          role: 'agent',
          text: message,
          created_at: new Date().toISOString(),
        },
      ]);

      if (data?.handover) {
        setHandover(data.handover);
      }

      setReply('');
      setMsg('');
      scrollToBottom();

      await load({ silent: true });
    } catch (e) {
      setMsg(e?.response?.data?.error || 'Failed to send reply.');
    } finally {
      setSending(false);
    }
  };

  const pickChat = async () => {
    if (!handover?.id) return;

    try {
      const { data } = await api.post(`/handover/${handover.id}/pick/`, {
        agent_id: selectedAgentId || undefined,
      });

      if (data?.handover) {
        setHandover(data.handover);
      }

      await load({ silent: true });
    } catch (e) {
      setMsg(e?.response?.data?.error || 'Failed to pick handover.');
    }
  };

  const resolveChat = async () => {
    if (!handover?.id) return;

    try {
      const { data } = await api.post(`/handover/${handover.id}/resolve/`, {});

      if (data?.handover) {
        setHandover(data.handover);
      }

      await load({ silent: true });
    } catch (e) {
      setMsg(e?.response?.data?.error || 'Failed to resolve handover.');
    }
  };

  useEffect(() => {
    loadAgents();
    load();
  }, [loadAgents, load]);

  useEffect(() => {
    if (!handoverId) return undefined;

    const timer = setInterval(() => {
      load({ silent: true });
    }, 8000);

    return () => clearInterval(timer);
  }, [handoverId, load]);

  return (
    <AdminLayout
      title={`Handover Thread #${handoverId}`}
      subtitle="View the customer conversation, pick an agent, reply on behalf of an agent, and resolve the handover."
    >
      {msg ? <Alert variant="danger">{msg}</Alert> : null}

      <Card className="admin-surface-card mb-3">
        <Card.Body className="d-flex justify-content-between align-items-center flex-wrap gap-3">
          <div>
            <div className="fw-semibold">
              Requester: {handover?.requester_name || handover?.requester_email || 'Guest'}
            </div>
            <div className="small text-muted">
              Channel: {handover?.channel || '-'} · Status: {handover?.status || '-'}
            </div>
            <div className="small text-muted">
              Thread: {handover?.thread_id || '-'}
            </div>
            <div className="small text-muted">
              Picked Agent: {handover?.picked_by_name || 'Unassigned'}
            </div>
          </div>

          <div className="d-flex gap-2 flex-wrap align-items-center">
            <Form.Select
              size="sm"
              value={selectedAgentId}
              onChange={(e) => setSelectedAgentId(e.target.value)}
              style={{ minWidth: 260 }}
            >
              <option value="">Select agent to reply as</option>
              {agents.map((agent) => (
                <option key={agent.id} value={agent.id}>
                  {agent.name} ({agent.email}){agent.is_online ? ' · Online' : ''}
                </option>
              ))}
            </Form.Select>

            {handover?.status === 'pending' ? (
              <Button size="sm" onClick={pickChat}>
                Pick Chat
              </Button>
            ) : null}

            {handover && !['resolved', 'cancelled'].includes(handover?.status) ? (
              <Button size="sm" variant="outline-success" onClick={resolveChat}>
                Resolve
              </Button>
            ) : null}

            <Button size="sm" variant="outline-primary" onClick={() => load({ silent: true })}>
              Refresh
            </Button>

            <Button as={Link} to="/admin/agent-inbox" size="sm" variant="outline-secondary">
              Back to Inbox
            </Button>
          </div>
        </Card.Body>
      </Card>

      <Card className="admin-surface-card">
        <Card.Body
          ref={listRef}
          style={{
            minHeight: 460,
            maxHeight: '70vh',
            overflowY: 'auto',
            background: 'linear-gradient(180deg, #f8fafc 0%, #eef2ff 100%)',
          }}
        >
          {initialLoading ? (
            <div className="text-center py-5">
              <Spinner animation="border" />
            </div>
          ) : (
            <div className="d-flex flex-column gap-3">
              {messages.map((m) => (
                <MessageBubble key={m.id} message={m} />
              ))}

              {!messages.length && (
                <div className="text-muted">
                  No conversation messages found.
                </div>
              )}
            </div>
          )}
        </Card.Body>

        <Card.Footer className="bg-white border-0 border-top">
          <Form onSubmit={(e) => { e.preventDefault(); sendReply(); }}>
            <div className="d-flex flex-column flex-md-row gap-2">
              <Form.Control
                as="textarea"
                rows={1}
                placeholder={
                  selectedAgent
                    ? `Reply as ${selectedAgent.name}...`
                    : 'Select an agent or reply using the picked agent...'
                }
                value={reply}
                disabled={!handover || ['resolved', 'cancelled'].includes(handover?.status)}
                onChange={(e) => setReply(e.target.value)}
                style={{
                  minHeight: 44,
                  maxHeight: 140,
                  resize: 'vertical',
                  borderRadius: 14,
                }}
              />

              <Button
                type="submit"
                disabled={sending || !reply.trim() || !handover || ['resolved', 'cancelled'].includes(handover?.status)}
                style={{
                  minWidth: 110,
                  borderRadius: 14,
                }}
              >
                {sending ? 'Sending...' : 'Send'}
              </Button>
            </div>
          </Form>
        </Card.Footer>
      </Card>
    </AdminLayout>
  );
}